import React, { useState, useEffect, useRef } from 'react';
import { rentalApi, fairManagerApi } from '../services/api';
import { FairManagerDashboardStats, Fair } from '../types';
import { DollarSign, TrendingUp, Clock, CheckCircle, AlertCircle, XCircle, Calendar, MapPin, Users, ChevronDown, Search, Filter, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { extractApiError } from '../utils/errorUtils';
import { formatCurrency } from '../utils/currency';
import { formatDateRange, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

const FairManagerDashboard: React.FC = () => {
  const navigate = useNavigate();
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const [stats, setStats] = useState<FairManagerDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fairs state
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [filteredFairs, setFilteredFairs] = useState<Fair[]>([]);
  const [fairsLoading, setFairsLoading] = useState(true);
  const [fairsError, setFairsError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'published' | 'upcoming' | 'past'>('all');
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    // Load from localStorage or default to 12
    try {
      const saved = localStorage.getItem('fairManagerDashboard_itemsPerPage');
      return saved ? parseInt(saved, 10) : 12;
    } catch (error) {
      console.warn('Failed to load items per page preference from localStorage:', error);
      return 12;
    }
  });

  useEffect(() => {
    loadDashboardStats();
    loadFairs();
  }, []);

  // Effect for filtering fairs based on search and status
  useEffect(() => {
    let filtered = fairs;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(fair =>
        fair.name.toLowerCase().includes(query) ||
        (fair.address?.city && fair.address.city.toLowerCase().includes(query)) ||
        (fair.address?.state && fair.address.state.toLowerCase().includes(query))
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(fair => {
        const status = getFairStatus(fair);
        return status === filterStatus;
      });
    }

    setFilteredFairs(filtered);
    // Reset to page 1 when filters change
    setCurrentPage(1);
  }, [fairs, searchQuery, filterStatus]);

  // Effect to save itemsPerPage to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('fairManagerDashboard_itemsPerPage', itemsPerPage.toString());
    } catch (error) {
      console.warn('Failed to save items per page preference to localStorage:', error);
    }
  }, [itemsPerPage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown !== null) {
        const dropdownElement = dropdownRefs.current[openDropdown];
        if (dropdownElement && !dropdownElement.contains(event.target as Node)) {
          setOpenDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await rentalApi.getDashboardStats();
      setStats(data);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load dashboard stats:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadFairs = async () => {
    try {
      setFairsLoading(true);
      setFairsError(null);
      const fairsData = await fairManagerApi.getFairs();
      setFairs(fairsData);
      setFilteredFairs(fairsData);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load fairs:', apiError);
      setFairsError(apiError.message);
    } finally {
      setFairsLoading(false);
    }
  };

  const getFairStatus = (fair: Fair): 'draft' | 'published' | 'upcoming' | 'past' => {
    const today = new Date();
    const startDate = parseDateOnly(fair.start_date);
    const endDate = parseDateOnly(fair.end_date);

    // Check if fair is in draft mode
    if (fair.is_draft) {
      return 'draft';
    }

    // Check if fair has ended
    if (endDate && endDate < today) {
      return 'past';
    }

    // Check if fair has started
    if (startDate && startDate > today) {
      return 'upcoming';
    }

    // Fair is currently running
    return 'published';
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      draft: { text: 'Draft', class: 'bg-gray-100 text-gray-800 border-gray-300' },
      published: { text: 'Live', class: 'bg-green-100 text-green-800 border-green-300' },
      upcoming: { text: 'Upcoming', class: 'bg-moonbone text-ink border-teal-300' },
      past: { text: 'Past', class: 'bg-purple-100 text-purple-800 border-purple-300' },
    };
    return badges[status as keyof typeof badges] || badges.draft;
  };

  const getCompletenessWarnings = (fair: Fair): string[] => {
    const warnings: string[] = [];

    if (!fair.total_slots || fair.total_slots === 0) {
      warnings.push('No slots configured');
    }

    if (!fair.start_date || !fair.end_date) {
      warnings.push('Missing dates');
    }

    if (!fair.address?.city || !fair.address?.state) {
      warnings.push('Missing location');
    }

    return warnings;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col items-center justify-center min-h-[400px]" data-testid="dashboard-loading">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={loadDashboardStats}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
            <p className="text-yellow-700">No dashboard data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      {/* Page Header */}
      <div className="bg-white shadow-sm rounded-lg p-3 mb-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-base sm:text-xl font-bold text-gray-900">Fair Manager Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-600">
            Financial overview and payout tracking for your managed fairs
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Total Revenue Card */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg shadow p-6 border border-green-200" data-testid="stat-total-revenue">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-green-800">Total Revenue</h3>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-900" data-testid="stat-value">{formatCurrency(stats.total_gross_revenue)}</p>
          <p className="text-xs text-green-700 mt-1">Gross from paid reservations</p>
        </div>

        {/* Commission Card */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg shadow p-6 border border-orange-200" data-testid="stat-platform-commission">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-orange-800">Platform Commission</h3>
            <TrendingUp className="h-8 w-8 text-orange-600" />
          </div>
          <p className="text-3xl font-bold text-orange-900" data-testid="stat-value">{formatCurrency(stats.platform_commission)}</p>
          <p className="text-xs text-orange-700 mt-1">{stats.commission_rate}% of gross revenue</p>
        </div>

        {/* Net Payout Card */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone rounded-lg shadow p-6 border border-teal-200" data-testid="stat-net-payout">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-ink">Net Payout</h3>
            <DollarSign className="h-8 w-8 text-secondary" />
          </div>
          <p className="text-3xl font-bold text-ink" data-testid="stat-value">{formatCurrency(stats.net_payout)}</p>
          <p className="text-xs text-primary-dark mt-1">After platform commission</p>
        </div>

        {/* Pending Approvals Card */}
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg shadow p-6 border border-yellow-200" data-testid="stat-pending-approvals">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-yellow-800">Pending Approvals</h3>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
          <p className="text-3xl font-bold text-yellow-900" data-testid="stat-value">{stats.pending_approvals}</p>
          <p className="text-xs text-yellow-700 mt-1">Reservations awaiting approval</p>
        </div>
      </div>

      {/* Reservations Overview */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Reservations Overview</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border-l-4 border-yellow-500 bg-yellow-50 p-4 rounded">
            <div className="flex items-center mb-2">
              <Clock className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-sm font-medium text-yellow-800">Pending</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900">{stats.total_pending_reservations}</p>
          </div>

          <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
            <div className="flex items-center mb-2">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <span className="text-sm font-medium text-green-800">Confirmed</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{stats.total_confirmed_reservations}</p>
          </div>

          <div className="border-l-4 border-dusty-teal bg-moonbone p-4 rounded">
            <div className="flex items-center mb-2">
              <DollarSign className="h-5 w-5 text-secondary mr-2" />
              <span className="text-sm font-medium text-ink">Paid</span>
            </div>
            <p className="text-2xl font-bold text-ink">{stats.total_paid_reservations}</p>
          </div>

          <div className="border-l-4 border-red-500 bg-red-50 p-4 rounded">
            <div className="flex items-center mb-2">
              <XCircle className="h-5 w-5 text-red-600 mr-2" />
              <span className="text-sm font-medium text-red-800">Cancelled</span>
            </div>
            <p className="text-2xl font-bold text-red-900">{stats.total_cancelled_reservations}</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/fairmanager/payouts"
            className="flex items-center justify-between p-4 bg-teal-50 hover:bg-secondary-light/100 border border-teal-200 rounded-lg transition-colors"
          >
            <div>
              <h3 className="font-semibold text-teal-900">View Payout History</h3>
              <p className="text-sm text-primary-dark">Detailed commission breakdown</p>
            </div>
            <TrendingUp className="h-6 w-6 text-secondary" />
          </Link>

          <Link
            to="/fairmanager/reservations"
            className="flex items-center justify-between p-4 bg-moonbone hover:bg-moonbone border border-teal-200 rounded-lg transition-colors"
          >
            <div>
              <h3 className="font-semibold text-ink">Manage Reservations</h3>
              <p className="text-sm text-primary-dark">Approve or review reservations</p>
            </div>
            <CheckCircle className="h-6 w-6 text-secondary" />
          </Link>

          <Link
            to="/fairmanager/fairs"
            className="flex items-center justify-between p-4 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
          >
            <div>
              <h3 className="font-semibold text-green-900">Manage Fairs</h3>
              <p className="text-sm text-green-700">Edit fair details and settings</p>
            </div>
            <DollarSign className="h-6 w-6 text-green-600" />
          </Link>
        </div>
      </div>

      {/* Your Fairs Section */}
      <div className="bg-white rounded-lg shadow p-6 mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Your Fairs</h2>
          <Link
            to="/fairmanager/fairs/create"
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            data-testid="create-fair-button"
          >
            Create New Fair
          </Link>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search fairs by name or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              data-testid="fair-search-input"
            />
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
                data-testid="fair-status-filter"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Live</option>
                <option value="upcoming">Upcoming</option>
                <option value="past">Past</option>
              </select>
            </div>
          </div>
        </div>

        {/* Fairs Loading State */}
        {fairsLoading && (
          <div className="flex justify-center items-center py-12" data-testid="fairs-loading">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Fairs Error State */}
        {fairsError && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded" data-testid="fairs-error">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{fairsError}</p>
            </div>
            <button
              onClick={loadFairs}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!fairsLoading && !fairsError && filteredFairs.length === 0 && (
          <div className="text-center py-12" data-testid="fairs-empty-state">
            <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">
              {searchQuery || filterStatus !== 'all'
                ? 'No fairs match your search criteria'
                : 'You have no fairs yet'}
            </p>
            {!searchQuery && filterStatus === 'all' && (
              <Link
                to="/fairmanager/fairs/create"
                className="inline-block mt-4 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              >
                Create Your First Fair
              </Link>
            )}
          </div>
        )}

        {/* Fair Cards Grid */}
        {!fairsLoading && !fairsError && filteredFairs.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="fairs-grid">
              {(() => {
                // Calculate pagination for current page
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = startIndex + itemsPerPage;
                const paginatedFairs = filteredFairs.slice(startIndex, endIndex);

                return paginatedFairs.map((fair) => {
              const status = getFairStatus(fair);
              const statusBadge = getStatusBadge(status);
              const warnings = getCompletenessWarnings(fair);

              return (
                <div
                  key={fair.id}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-lg transition-shadow bg-white"
                  data-testid={`fair-card-${fair.id}`}
                >
                  {/* Fair Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0 mr-2">
                      <h3 className="font-bold text-lg text-gray-900 truncate" data-testid="fair-card-name">
                        {fair.name}
                      </h3>
                      <span
                        className={`inline-block px-2 py-1 text-xs font-medium rounded border mt-1 ${statusBadge.class}`}
                        data-testid="fair-card-status"
                      >
                        {statusBadge.text}
                      </span>
                    </div>

                    {/* Manage Dropdown */}
                    <div className="relative" ref={(el) => (dropdownRefs.current[fair.id] = el)}>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === fair.id ? null : fair.id)}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid={`fair-dropdown-button-${fair.id}`}
                        aria-label="Manage fair options"
                      >
                        <ChevronDown className="h-5 w-5 text-gray-600" />
                      </button>

                      {openDropdown === fair.id && (
                        <div
                          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10"
                          data-testid={`fair-dropdown-menu-${fair.id}`}
                        >
                          <div className="py-1">
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/edit`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-edit"
                            >
                              Edit Fair Details
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/slots`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-slots"
                            >
                              Manage Slots
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/booth-layouts`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-booth-layouts"
                            >
                              Booth Layouts
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/amenities`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-amenities"
                            >
                              Amenities Manager
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/availability`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-availability"
                            >
                              Slot Availability
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/pricing`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-pricing"
                            >
                              Pricing Templates
                            </button>
                            <button
                              onClick={() => navigate(`/fairmanager/fairs/${fair.id}/contact-settings`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm"
                              data-testid="dropdown-contact"
                            >
                              Contact Settings
                            </button>
                            <button
                              onClick={() => navigate(`/fair/${fair.id}`)}
                              className="w-full text-left px-4 py-2 hover:bg-gray-100 text-gray-700 text-sm border-t border-gray-200"
                              data-testid="dropdown-view"
                            >
                              View Fair Page
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Fair Details */}
                  <div className="space-y-2 mb-4">
                    {fair.start_date && fair.end_date && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">
                          {formatDateRange(fair.start_date, fair.end_date)}
                        </span>
                      </div>
                    )}

                    {(fair.address?.city || fair.address?.state) && (
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span className="truncate">
                          {fair.address?.city && fair.address?.state
                            ? `${fair.address.city}, ${fair.address.state}`
                            : fair.address?.city || fair.address?.state || 'Location pending'}
                        </span>
                      </div>
                    )}

                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                      <span>
                        {fair.total_slots || 0} slot{fair.total_slots !== 1 ? 's' : ''}
                        {fair.available_slots !== undefined && (
                          <span className="text-green-600 ml-1">
                            ({fair.available_slots} available)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Completeness Warnings */}
                  {warnings.length > 0 && (
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded" data-testid="fair-card-warnings">
                      <div className="flex items-start">
                        <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-yellow-800 mb-1">Incomplete Setup</p>
                          <ul className="text-xs text-yellow-700 space-y-0.5">
                            {warnings.map((warning, idx) => (
                              <li key={idx}>• {warning}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
                });
              })()}
            </div>

            {/* Pagination Controls */}
            {(() => {
              const totalPages = Math.ceil(filteredFairs.length / itemsPerPage);
              const startIndex = (currentPage - 1) * itemsPerPage;
              const endIndex = Math.min(startIndex + itemsPerPage, filteredFairs.length);

              return (
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="pagination-controls">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <label htmlFor="items-per-page" className="text-sm text-gray-700">
                      Items per page:
                    </label>
                    <select
                      id="items-per-page"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1); // Reset to page 1 when changing items per page
                      }}
                      className="border border-gray-300 rounded px-3 py-1 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                      data-testid="items-per-page-selector"
                    >
                      <option value={6}>6</option>
                      <option value={12}>12</option>
                      <option value={24}>24</option>
                      <option value={48}>48</option>
                    </select>
                  </div>

                  {/* Page info and navigation */}
                  <div className="flex items-center gap-4">
                    {/* Current page info */}
                    <span className="text-sm text-gray-700" data-testid="pagination-info">
                      Showing {startIndex + 1}-{endIndex} of {filteredFairs.length} fair{filteredFairs.length !== 1 ? 's' : ''}
                    </span>

                    {/* Page navigation buttons */}
                    <div className="flex items-center gap-1">
                      {/* Previous button */}
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`p-2 rounded ${
                          currentPage === 1
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid="pagination-prev"
                        aria-label="Previous page"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>

                      {/* Page number buttons */}
                      {(() => {
                        const pageButtons = [];
                        const maxVisiblePages = 5;

                        if (totalPages <= maxVisiblePages) {
                          // Show all pages if total is small
                          for (let i = 1; i <= totalPages; i++) {
                            pageButtons.push(
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`px-3 py-1 rounded text-sm ${
                                  currentPage === i
                                    ? 'bg-primary text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                data-testid={`pagination-page-${i}`}
                              >
                                {i}
                              </button>
                            );
                          }
                        } else {
                          // Show ellipsis for large page counts
                          // Always show first page
                          pageButtons.push(
                            <button
                              key={1}
                              onClick={() => setCurrentPage(1)}
                              className={`px-3 py-1 rounded text-sm ${
                                currentPage === 1
                                  ? 'bg-primary text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              data-testid="pagination-page-1"
                            >
                              1
                            </button>
                          );

                          // Show ellipsis if needed
                          if (currentPage > 3) {
                            pageButtons.push(
                              <span key="ellipsis1" className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          // Show pages around current page
                          const start = Math.max(2, currentPage - 1);
                          const end = Math.min(totalPages - 1, currentPage + 1);
                          for (let i = start; i <= end; i++) {
                            pageButtons.push(
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`px-3 py-1 rounded text-sm ${
                                  currentPage === i
                                    ? 'bg-primary text-white'
                                    : 'text-gray-700 hover:bg-gray-100'
                                }`}
                                data-testid={`pagination-page-${i}`}
                              >
                                {i}
                              </button>
                            );
                          }

                          // Show ellipsis if needed
                          if (currentPage < totalPages - 2) {
                            pageButtons.push(
                              <span key="ellipsis2" className="px-2 text-gray-500">
                                ...
                              </span>
                            );
                          }

                          // Always show last page
                          pageButtons.push(
                            <button
                              key={totalPages}
                              onClick={() => setCurrentPage(totalPages)}
                              className={`px-3 py-1 rounded text-sm ${
                                currentPage === totalPages
                                  ? 'bg-primary text-white'
                                  : 'text-gray-700 hover:bg-gray-100'
                              }`}
                              data-testid={`pagination-page-${totalPages}`}
                            >
                              {totalPages}
                            </button>
                          );
                        }

                        return pageButtons;
                      })()}

                      {/* Next button */}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded ${
                          currentPage === totalPages
                            ? 'text-gray-400 cursor-not-allowed'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                        data-testid="pagination-next"
                        aria-label="Next page"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default FairManagerDashboard;
